import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapComponent.css";
import { BiLock, BiLockOpen } from "react-icons/bi";

// Handle map click events
const MapClickHandler = ({ onClick }) => {
    const map = useMapEvents({
        click: (e) => {
            if (typeof onClick === "function") {
                onClick(e.latlng);
            }
        }
    });
    return null;
};

// Custom CSS for shop markers
const shopMarkerStyles = `
    .custom-shop-marker {
        background: transparent !important;
        border: none !important;
    }
    
    .custom-shop-marker div {
        transition: all 0.2s ease;
        cursor: pointer;
    }
    
    .custom-shop-marker:hover div {
        transform: scale(1.2);
        box-shadow: 0 4px 8px rgba(0,0,0,0.4) !important;
    }
    
    .custom-shop-marker div::after {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border: 2px solid rgba(255,255,255,0.3);
        border-radius: 50%;
        animation: pulse 2s infinite;
    }
    
    @keyframes pulse {
        0% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.1);
            opacity: 0.7;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
    
    .leaflet-popup-content-wrapper {
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
    
    .leaflet-popup-tip {
        background: white;
    }
    
    .leaflet-popup {
        pointer-events: auto !important;
    }
    
    .leaflet-popup-content {
        margin: 0 !important;
        pointer-events: auto !important;
    }
    
    .leaflet-popup-close-button {
        display: none !important;
    }
`;

// Fix for Leaflet's default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Editable Polygon Component
const EditablePolygon = ({ positions, onEdit, isEditMode, title, color }) => {
    const map = useMap();
    const polygonRef = useRef();
    // Removed local state to prevent sync issues and double renders

    const handleVertexDrag = (index, newLatLng) => {
        const newPositions = [...positions];
        newPositions[index] = {
            lat: newLatLng.lat,
            lng: newLatLng.lng
        };
        onEdit(newPositions);
    };

    const handleVertexRightClick = (e, index) => {
        if (!isEditMode) return;
        e.originalEvent.preventDefault();

        if (positions.length <= 3) {
            alert("Cannot delete vertex. Polygon must have at least 3 points.");
            return;
        }

        const newPositions = positions.filter((_, i) => i !== index);
        onEdit(newPositions);
    };

    return (
        <>
            <Polygon
                ref={polygonRef}
                positions={positions.map(pos => [pos.lat, pos.lng])}
                pathOptions={{
                    color: color || "white",
                    fillColor: "#000000",
                    fillOpacity: 0.3,
                    weight: isEditMode ? 3 : 1,
                    dashArray: isEditMode ? "5, 5" : "2, 2",
                }}
            >
                <Popup>{title}</Popup>
            </Polygon>

            {/* Show vertices when in edit mode */}
            {isEditMode && positions.map((pos, index) => (
                <Marker
                    key={index}
                    position={[pos.lat, pos.lng]}
                    icon={L.divIcon({
                        className: 'bg-red-500 w-4 h-4 rounded-full border-2 border-white cursor-move hover:bg-red-600 transition-colors duration-200',
                        iconSize: [16, 16]
                    })}
                    draggable={true}
                    eventHandlers={{
                        dragend: (e) => handleVertexDrag(index, e.target.getLatLng()),
                        contextmenu: (e) => handleVertexRightClick(e, index)
                    }}
                />
            ))}
        </>
    );
};

// Map Double Click Handler Component
const MapDoubleClickHandler = ({ isEditMode, onAddPoint, polygonPositions }) => {
    const map = useMap();

    useEffect(() => {
        if (!isEditMode) return;

        const handleMapDoubleClick = (e) => {
            onAddPoint(e.latlng);
        };

        map.on('dblclick', handleMapDoubleClick);

        return () => {
            map.off('dblclick', handleMapDoubleClick);
        };
    }, [isEditMode, onAddPoint, map]);

    return null;
};

// Map Interaction Lock Component
const LockMapInteraction = ({ isLocked }) => {
    const map = useMap();

    useEffect(() => {
        if (isLocked) {
            map.dragging.disable();
            map.scrollWheelZoom.disable();
            map.doubleClickZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
        } else {
            map.dragging.enable();
            map.scrollWheelZoom.enable();
            map.doubleClickZoom.enable();
            map.boxZoom.enable();
            map.keyboard.enable();
        }
    }, [isLocked, map]);

    return null;
};

// Map View Updater Component
const MapUpdater = ({ newCenter, zoom }) => {
    const map = useMap();

    useEffect(() => {
        if (newCenter) {
            map.setView(newCenter, zoom);
        }
    }, [newCenter, zoom, map]);

    return null;
};

// Resize Fix Component
const ResizeFix = () => {
    const map = useMap();

    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                map.invalidateSize(); // Recalculate map size
            } catch (e) {
                console.warn("Map invalidateSize failed", e);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [map]);

    return null;
};

const MapComponent = ({
    markersList = [],
    areasList = [],
    boundaryColors = ["#2FB95D"],
    mapLocation = [22.7196, 75.8577],
    zoomLevel = 13,
    height = "500px",
    isEditMode = false,
    onPolygonEdit = () => { },
    onMarkerClick = () => { }, // Add custom marker click handler
    onMapClick, // optional map click handler
    satelliteTileUrl = null, // Add new prop for satellite tiles
}) => {
    const [tileLayerIndex, setTileLayerIndex] = useState(0);
    const [isLocked, setIsLocked] = useState(false);
    const [tempMarker, setTempMarker] = useState(null);

    // Effect to clear temp marker after delay
    useEffect(() => {
        if (tempMarker) {
            const timer = setTimeout(() => {
                setTempMarker(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [tempMarker]);

    const tileLayers = [
        {
            name: "Satellite",
            url: "https://api.maptiler.com/maps/satellite/256/{z}/{x}/{y}.jpg?key=uFt2BoWi8PwTxvc35csD",
        },
        {
            name: "OSM",
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        },
        {
            name: "CaLi",
            url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        },
        {
            name: "CaDk",
            url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        },
    ];

    // Function to cycle through tile layers
    const toggleLayer = () => {
        setTileLayerIndex((prevIndex) => (prevIndex + 1) % tileLayers.length);
    };

    // Function to toggle lock state
    const toggleLock = () => {
        setIsLocked((prev) => !prev);
    };

    const getPolygonCenter = (points) => {
        const latSum = points.reduce((sum, point) => sum + point.lat, 0);
        const lngSum = points.reduce((sum, point) => sum + point.lng, 0);
        return [latSum / points.length, lngSum / points.length];
    };

    // Handle adding new points to polygons
    const handleAddPoint = (latlng) => {
        if (!isEditMode || areasList.length === 0) return;

        // Find the first polygon and add the point to it
        const firstArea = areasList[0];
        if (firstArea && firstArea.area && firstArea.area.length > 0) {
            const newPoint = {
                lat: latlng.lat,
                lng: latlng.lng
            };

            // Find the closest segment and insert the new point
            const positions = firstArea.area;
            let minDistance = Infinity;
            let insertIndex = 0;

            for (let i = 0; i < positions.length; i++) {
                const nextIndex = (i + 1) % positions.length;
                const segmentStart = positions[i];
                const segmentEnd = positions[nextIndex];

                const distance = distanceToSegment(
                    newPoint.lat, newPoint.lng,
                    segmentStart.lat, segmentStart.lng,
                    segmentEnd.lat, segmentEnd.lng
                );

                if (distance < minDistance) {
                    minDistance = distance;
                    insertIndex = nextIndex;
                }
            }

            const newPositions = [...positions];
            newPositions.splice(insertIndex, 0, newPoint);

            // Show visual feedback
            console.log(`Added new point at (${newPoint.lat}, ${newPoint.lng})`);

            // Show temporary marker
            // Show temporary marker
            setTempMarker(newPoint);

            // Update the polygon through the callback
            onPolygonEdit(newPositions);
        }
    };

    // Helper function to calculate distance to segment
    const distanceToSegment = (px, py, x1, y1, x2, y2) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;

        if (lenSq !== 0) param = dot / lenSq;

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = px - xx;
        const dy = py - yy;

        return Math.sqrt(dx * dx + dy * dy);
    };

    return (
        <>
            <style>{shopMarkerStyles}</style>
            <div className="map-container" style={{ height: height, width: "100%" }}>
                {/* Layer Toggle Button */}
                <button
                    onClick={toggleLayer}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        zIndex: 1000,
                        background: "#fff",
                        padding: "8px 12px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        cursor: "pointer",
                        fontSize: "14px",
                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                    }}
                >
                    {tileLayers[tileLayerIndex].name}
                </button>

                {/* Lock/Unlock Button - Only show when not in edit mode */}
                {!isEditMode && (
                    <div
                        onClick={toggleLock}
                        style={{
                            position: "absolute",
                            top: "60px",
                            right: "10px",
                            zIndex: 1000,
                            padding: "8px 12px",
                            borderRadius: "5px",
                            border: "1px solid #ccc",
                            cursor: "pointer",
                            fontSize: "14px",
                            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        className={`text-sm ${isLocked ? "text-white" : "text-green-600"} font-bold ${isLocked ? "bg-primary" : "bg-white"}`}
                    >
                        <span className={`mr-1 ${isLocked ? "text-white" : "text-green-600"} font-bold`}>
                            {isLocked ? <BiLockOpen size={20} /> : <BiLock size={20} />}
                        </span>
                        {isLocked ? "Unlock" : "Lock"}
                    </div>
                )}

                {/* Edit Mode Instructions */}
                {isEditMode && (
                    <div
                        style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            zIndex: 1000,
                            background: "rgba(0, 0, 0, 0.9)",
                            color: "white",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            fontSize: "13px",
                            maxWidth: "220px",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                    >
                        <div className="font-bold mb-2 text-blue-300">✏️ Edit Mode Active</div>
                        <div className="mb-1">• <strong>Drag</strong> red vertices to move</div>
                        <div className="mb-1">• <strong>Double-click</strong> <span className="text-yellow-300">anywhere on map</span> to add point</div>
                        <div className="mb-1">• <strong>Right-click</strong> vertex to delete</div>
                        <div className="text-xs text-gray-300 mt-2">Minimum 3 points required</div>
                    </div>
                )}

                <MapContainer
                    center={mapLocation}
                    zoom={zoomLevel}
                    className="rounded-md"
                    style={{ maxHeight: "100%", height: "100%", width: "100%" }}
                    zoomAnimationDuration={800}
                    doubleClickZoom={!isEditMode}
                >
                    {/* Map Tile */}
                    <TileLayer url={tileLayers[tileLayerIndex].url} />

                    {/* Satellite Tile Overlay */}
                    {satelliteTileUrl && (
                        <TileLayer
                            url={satelliteTileUrl}
                            opacity={0.8}
                            zIndex={100}
                            key={satelliteTileUrl} // Ensure refresh when url changes
                        />
                    )}

                    {/* Lock/Unlock Map Interactions */}
                    <LockMapInteraction isLocked={isLocked} />

                    {/* Map click handler */}
                    {onMapClick ? <MapClickHandler onClick={onMapClick} /> : null}

                    {/* Map Double Click Handler for adding points */}
                    <MapDoubleClickHandler
                        isEditMode={isEditMode}
                        onAddPoint={handleAddPoint}
                        polygonPositions={areasList[0]?.area || []}
                    />

                    {/* Markers */}
                    {markersList.map((marker, idx) => {
                        // Create custom marker icon based on marker data
                        let markerIcon;
                        if (marker.markerData) {
                            // Custom marker for shops with different colors
                            const isApproved = marker.markerData.approved;
                            const color = isApproved ? '#10B981' : '#F59E0B'; // Green for approved, amber for pending

                            markerIcon = L.divIcon({
                                className: 'custom-shop-marker',
                                html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); cursor: pointer;" title="Click to view shop details"></div>`,
                                iconSize: [24, 24],
                                iconAnchor: [12, 12]
                            });
                        } else {
                            // Default marker icon
                            markerIcon = L.icon({
                                iconUrl: marker.icon || require("leaflet/dist/images/marker-icon.png"),
                                iconSize: [36, 52],
                            });
                        }

                        return (
                            <Marker
                                key={idx}
                                position={[marker.lat, marker.lng]}
                                icon={markerIcon}
                                eventHandlers={{
                                    // Remove the click handler - let the tooltip handle navigation
                                    // click: () => onMarkerClick(marker)
                                }}
                            >
                                <Popup>
                                    {marker.markerData ? (
                                        // Custom tooltip for shops
                                        <div className="bg-white p-4 rounded-lg shadow-lg border max-w-xs">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {marker.markerData.logo && (
                                                        <img
                                                            src={marker.markerData.logo}
                                                            alt={marker.markerData.name}
                                                            className="w-12 h-12 rounded-full object-cover border"
                                                        />
                                                    )}
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-gray-800 text-sm leading-tight">{marker.markerData.name}</h4>
                                                        <p className="text-xs text-gray-600 mt-1">{marker.markerData.owner_name}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Close the popup by clicking outside
                                                        const popup = e.target.closest('.leaflet-popup');
                                                        if (popup) {
                                                            popup.remove();
                                                        }
                                                    }}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="mb-3">
                                                <p className="text-xs text-gray-600 mb-2">
                                                    <strong>Address:</strong> {marker.markerData.address?.state || 'Not available'}
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    <strong>ID:</strong> {marker.markerData.id}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${marker.markerData.approved
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-amber-100 text-amber-800'
                                                    }`}>
                                                    {marker.markerData.approved ? '✓ Approved' : '⏳ Pending'}
                                                </span>
                                                <button
                                                    onClick={() => onMarkerClick(marker)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/80 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    Edit Shop
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // Default tooltip
                                        <div>{marker.label || `Marker at (${marker.lat}, ${marker.lng})`}</div>
                                    )}
                                </Popup>
                            </Marker>
                        );
                    })}

                    {/* Temporary marker for new points */}
                    {tempMarker && (
                        <Marker
                            position={[tempMarker.lat, tempMarker.lng]}
                            icon={L.divIcon({
                                className: 'bg-green-500 w-6 h-6 rounded-full border-2 border-white animate-pulse',
                                iconSize: [24, 24]
                            })}
                        >
                            <Popup>New point added!</Popup>
                        </Marker>
                    )}

                    {areasList.map((area, idx) => {
                        if (!area.area || area.area.length === 0) return null; // Skip if no valid coordinates

                        const center = getPolygonCenter(area.area);

                        return (
                            <div key={idx}>
                                <EditablePolygon
                                    positions={area.area}
                                    onEdit={onPolygonEdit}
                                    isEditMode={isEditMode}
                                    title={area.title}
                                    color={boundaryColors[idx] || "white"}
                                />
                                <Marker
                                    position={center}
                                    icon={L.divIcon({
                                        className: 'text-yellow-300 text-[.5rem] font-bold',
                                        html: `<div>${area.title}</div>`,
                                    })}
                                    interactive={false}
                                />
                            </div>
                        );
                    })}

                    {/* Dynamic Map Center Update */}
                    <MapUpdater newCenter={mapLocation} zoom={zoomLevel} />

                    {/* Resize Fix */}
                    {/* <ResizeFix /> */}
                </MapContainer>
            </div>
        </>
    );
};

export default MapComponent;
